import "./styles.css";
import "./sectionStyles.css";
import "./communityStyles.css";
import "./rewardTransferStyles.css";
import "./marketStyles.css";
import "./treasuryStyles.css";
import "./androidReliability.css";

import { BufferedGameAnalytics } from "./adapters/BufferedGameAnalytics";
import { BrowserDelay } from "./adapters/BrowserDelay";
import { LocalStorageGameRepository } from "./adapters/LocalStorageGameRepository";
import { SystemServerClock } from "./adapters/SystemServerClock";
import { GameApp } from "./app/GameApp";
import { browserDiagnosticsContext } from "./domain/community";
import { DEV_TOOLS_BUILD_ENABLED } from "./config/buildFeatures";
import type { GameServicePort } from "./domain/ports";
import { HttpGameService } from "./services/HttpGameService";
import { MockGameService } from "./services/MockGameService";
import { installTelegramViewport } from "./ui/telegramViewport";
import { installRuntimeDiagnostics } from "./ui/runtimeDiagnostics";
import { GAME_CONFIG } from "./config/gameConfig";

// Local visual rehearsal only; production always uses its published API rules.
if (import.meta.env.DEV && new URLSearchParams(location.search).get("season") === "2") {
  const { SEASON_TWO_CONFIG } = await import("./config/seasonTwo");
  Object.assign(GAME_CONFIG, SEASON_TWO_CONFIG);
}

const root = document.querySelector<HTMLElement>("#app");
if (root === null) throw new Error("App root is missing");

const dismissBootSplash = () => {
  const bootSplash = document.querySelector<HTMLElement>("#boot-splash");
  if (bootSplash === null || bootSplash.classList.contains("is-done")) return;
  bootSplash.classList.add("is-done");
  window.setTimeout(() => bootSplash.remove(), 360);
};
window.addEventListener("diamond-tapper-ready", dismissBootSplash);

const removeTelegramViewport = installTelegramViewport();
const lastSuccessfulRequestSample = new Map<string, number>();
const analytics = new BufferedGameAnalytics({
  context: browserDiagnosticsContext,
  sink: (events) => service.sendDiagnostics(events),
});
const service: GameServicePort = import.meta.env.DEV
  ? new MockGameService({
      repository: new LocalStorageGameRepository(),
      clock: new SystemServerClock(),
      delay: new BrowserDelay(),
      initialSettings: {
        reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      },
    })
  : new HttpGameService({
      onRequestMetric: (metric) => {
        // Never let an analytics request generate another analytics request.
        // One fast success per route/minute; retain failures and slow requests.
        // Diagnostics must not create a database write for each tap batch.
        if (metric.route.includes("/diagnostics/")) return;
        if (metric.ok && metric.durationMs < 1500) {
          const now = Date.now();
          if (now - (lastSuccessfulRequestSample.get(metric.route) ?? 0) < 60_000) return;
          if (lastSuccessfulRequestSample.size > 100) lastSuccessfulRequestSample.clear();
          lastSuccessfulRequestSample.set(metric.route, now);
        }
        analytics.track("request_complete", metric);
      },
    });
const devToolsRequested = DEV_TOOLS_BUILD_ENABLED
  ? new URLSearchParams(window.location.search).get("dev") === "1"
  : false;
const app = new GameApp({
  root,
  service,
  analytics,
  enableDevTools: DEV_TOOLS_BUILD_ENABLED && devToolsRequested,
});

const removeRuntimeDiagnostics = installRuntimeDiagnostics(window, (properties) => analytics.track("client_error", properties));
void app.start();

const diagnosticsTimer = window.setInterval(() => void analytics.flush().catch(() => undefined), 30_000);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") void analytics.flush().catch(() => undefined);
});

window.addEventListener("beforeunload", () => {
  window.clearInterval(diagnosticsTimer);
  app.destroy();
  removeTelegramViewport();
  removeRuntimeDiagnostics();
});
